import type { CatalogueKind, ProductClassificationKind } from '../enums'

import type { FieldKey } from './field-key'

/**
 * The four lists a product is filed under — coarsest first, the tier last —
 * with the product column each one fills, the URL parameter its catalogue
 * filter rides in, and the field key its label is read from.
 *
 * One table rather than four literals that have to stay in the same order:
 * the screen, the product sheet and the list filters all read it, so a fifth
 * axis is one entry here and cannot end up pointing at another axis' column.
 */
export const PRODUCT_CLASSIFICATION_AXES = {
  CATEGORY: {
    column: 'categoryId',
    filter: 'category',
    fieldKey: 'productCategory',
  },
  GROUP: { column: 'groupId', filter: 'group', fieldKey: 'productGroup' },
  DESIGN_LINE: {
    column: 'designLineId',
    filter: 'designLine',
    fieldKey: 'designLine',
  },
  STANDARD: {
    column: 'standardId',
    filter: 'standard',
    fieldKey: 'productStandard',
  },
} as const satisfies Record<
  ProductClassificationKind,
  { column: string; filter: string; fieldKey: FieldKey }
>

/** The same four in the same order, where only the member is needed. */
export const PRODUCT_CLASSIFICATION_KINDS = Object.keys(
  PRODUCT_CLASSIFICATION_AXES,
) as ProductClassificationKind[]

/**
 * The lists each branch of the catalogue tree is found by.
 *
 * A product carries all four axes; an Arbeitsleistung carries three — a design
 * line is a manufacturer's range and names nothing about work. The other three
 * branches are found by their folders and by what they are called, so they
 * carry none here — a measure's own `standardId` is a property of the measure
 * and not something its branch is narrowed by.
 *
 * One record rather than a list per branch, because three readers ask it: the
 * toolbar that offers the selects, the predicate that narrows the rows, and
 * the write guard that refuses an id from the wrong list. Written twice, a
 * bookmarked `?kind=LABOUR&designLine=7` narrowed the list to nothing with no
 * control on screen to clear it.
 */
export const CLASSIFICATION_KINDS_OF_BRANCH = {
  MEASURE: [],
  PRODUCT: PRODUCT_CLASSIFICATION_KINDS,
  LABOUR: ['CATEGORY', 'GROUP', 'STANDARD'],
  FORMULA: [],
  PRICE_BLOCK: [],
  PRICE_GROUP: [],
} as const satisfies Record<CatalogueKind, readonly ProductClassificationKind[]>

/**
 * The axes a product page can decide for itself.
 *
 * A category and a group are what the product *is*, and a shop states both. A
 * design line and a standard are this business's judgement about where the
 * product sits in its own range, so nothing on a page could fill them.
 *
 * Read by the extraction schema, by the service that maps the answer back to
 * an entry, and by the import form's rows.
 */
export const IMPORTED_CLASSIFICATION_KINDS = [
  'CATEGORY',
  'GROUP',
] as const satisfies readonly ProductClassificationKind[]

export type ImportedClassificationKind =
  (typeof IMPORTED_CLASSIFICATION_KINDS)[number]
